/**
 * Model-specific image constraints.
 *
 * Pure functions — no React, no hooks, trivially testable. Mirrors the shape of
 * videoConstraints.ts.
 *
 * NEITHER quality NOR aspect is hand-written here any more. Quality comes from
 * the generated `IMAGE_RESOLUTION_POLICY`; aspect comes from the generated
 * `offeredAspectRatios` on the capability record (RFC-1202 phase 4), which
 * removed the last hand-kept mirror of
 * `provider_models.IMAGE_MODEL_SUPPORTED_ASPECT_RATIOS`.
 *
 * Note the deliberate split: the registry declares every ratio the PROVIDER
 * publishes — Gemini publishes fourteen — while `offeredAspectRatios` is the
 * subset the picker shows. Preflight validates against the former, so a ratio
 * this file does not offer is still legal on a canvas that already carries it.
 *
 * Why per-model gating: image models do not accept the same QUALITY / ASPECT
 * options, and the ones they reject fail loudly at the provider:
 * - GPT Image 2 / 2.5 accept custom sizes within provider bounds. Every
 *   Dreamwrite label, at each of 1K / 2K / 4K, maps to an exact backend size
 *   (provider_models.GPT_IMAGE_2_SIZES_BY_RESOLUTION).
 * - Nano Banana (`gemini-2.5-flash-image`) is called with no image_size.
 * - Nano Banana 2 Lite (`gemini-3.1-flash-lite-image`) accepts **1K only** — 512P/2K/4K
 *   are 400 INVALID_ARGUMENT on Vertex (T283692034).
 * - Nano Banana Pro accepts the full grid.
 */

import { MODEL_CAPABILITIES } from '../../../../generated/modelCapabilities'
import {
  type AspectRatio,
  IMAGE_REFERENCE_POLICY,
  IMAGE_RESOLUTION_POLICY,
} from '../../../../generated/nodeDataTypes'
import { getOfferedAspectRatios, getOfferedAspectRatioDefault } from './generationPreflight'

/* ── Types ── */

export interface ImageConstraints {
  /** Selectable quality options, or null when the model uses a fixed resolution. */
  qualityOptions: string[] | null
  /** Value to PERSIST for in_quality (never persist the display label). */
  qualityDefault: string
  /** Static label shown when qualityOptions is null (e.g. "Fixed"). */
  qualityLabel?: string
  /** Aspect ratios the model can fulfil — the selector renders only these. */
  aspectRatioOptions: AspectRatio[]
  /** Default aspect ratio for this model. */
  aspectRatioDefault: AspectRatio
}

/* ── Constants ── */

export const GPT_IMAGE_2_MODEL = 'openai-gpt-image-2'
export const GEMINI_2_5_IMAGE_MODEL = 'gemini-2.5-flash-image'
export const MANGO_EMBER_DRIFT_MODEL = 'mango-ember-drift'

const FULL_QUALITY_OPTIONS = ['512P', '1K', '2K', '4K']
const DEFAULT_QUALITY = '1K'
const FIXED_QUALITY_LABEL = 'Fixed'

/**
 * Fail-OPEN grid for BYOM / unknown ids, where no record declares an offer.
 *
 * Not a mirror of any model: inventing a restriction for user-owned routing
 * would violate CLAUDE.md #8, so an unrecognized id gets the full product grid.
 */
export const FALLBACK_ASPECT_RATIOS: readonly AspectRatio[] = [
  '16:9',
  '9:16',
  '3:4',
  '4:3',
  '1:1',
]

/** Every value of the `AspectRatio` union, in picker display order. */
export const ASPECT_RATIO_VALUES: readonly AspectRatio[] = [
  'Auto',
  '1:1',
  '2:1',
  '4:3',
  '3:4',
  '5:4',
  '4:5',
  '3:2',
  '2:3',
  '16:9',
  '9:16',
  '21:9',
  '9:21',
]

/**
 * Narrow a declared ratio string to the union, without a cast.
 *
 * A predicate rather than an assertion because the registry is generated from
 * provider facts and could legitimately grow a ratio the console has no
 * geometry for. Casting would let such a value reach `ASPECT_NUMERIC`, come
 * back `undefined`, and make the snap helpers compare NaN distances — silently
 * picking an arbitrary ratio instead of failing.
 */
function isAspectRatio(value: string): value is AspectRatio {
  return ASPECT_RATIO_VALUES.some((candidate) => candidate === value)
}

/**
 * The declared offer for `model`, or the fail-open grid when there is none.
 *
 * Filtering is a no-op today — `imageConstraints.offeredAspectRatios.test.ts`
 * asserts every declared image offer is in the union — but it is what makes
 * the narrowing honest rather than assumed.
 */
function offeredAspectRatios(model: string): readonly AspectRatio[] {
  const declared = getOfferedAspectRatios(model)
  if (declared === null || declared.length === 0) return FALLBACK_ASPECT_RATIOS
  const known = declared.filter(isAspectRatio)
  return known.length > 0 ? known : FALLBACK_ASPECT_RATIOS
}

/** The declared picker default for `model`, narrowed without a cast. */
function offeredAspectRatioDefault(model: string): AspectRatio {
  const declared = getOfferedAspectRatioDefault(model)
  return declared !== null && isAspectRatio(declared) ? declared : '16:9'
}

/** Label for letting the provider choose the shape; it has no numeric ratio. */
export const AUTO_ASPECT_RATIO = 'Auto' satisfies AspectRatio

type FixedAspectRatio = Exclude<AspectRatio, typeof AUTO_ASPECT_RATIO>

const ASPECT_NUMERIC: Record<FixedAspectRatio, number> = {
  '1:1': 1,
  '2:1': 2,
  '4:3': 4 / 3,
  '3:4': 3 / 4,
  '5:4': 5 / 4,
  '4:5': 4 / 5,
  '3:2': 3 / 2,
  '2:3': 2 / 3,
  '16:9': 16 / 9,
  '9:16': 9 / 16,
  '21:9': 21 / 9,
  '9:21': 9 / 21,
}

/** Width / height of `ratio`, or `null` for `Auto`. */
export function aspectRatioValue(ratio: AspectRatio): number | null {
  return ratio === AUTO_ASPECT_RATIO ? null : ASPECT_NUMERIC[ratio]
}

/* ── Main Functions ── */

/**
 * Available quality/aspect options and defaults for a given image model.
 * Fail-OPEN for unknown / BYOM / internal-eval models (full options) — never
 * block a model we don't have explicit constraints for.
 */
/**
 * User-facing reference-image budget for `model`, or `null` when undeclared.
 *
 * `null` is fail-OPEN for BYOM / unknown ids (CLAUDE.md #8) — the caller keeps
 * whatever generic cap it already used rather than inventing a restriction.
 *
 * This is the USER budget (total minus the reserved style slot), which is what
 * a picker should enforce: the style preview occupies the remaining slot and
 * must survive a node saturated with character references.
 */
export function getImageReferenceLimit(model: string): number | null {
  return IMAGE_REFERENCE_POLICY[model]?.maxUser ?? null
}

/**
 * Slots held back from the provider budget for the style reference.
 *
 * The difference between what the provider accepts and what the picker lets
 * the user fill. Exposed rather than folded into the cap because the number is
 * only defensible if the UI can say WHY: 15 of 16 looks arbitrary until it says
 * the last slot is the style preview. That reservation is also what stops the
 * style reference being the first thing dropped — the failure RFC-1202 §2.3
 * calls out, where the prompt still said "Image N is the style reference"
 * after the style had been truncated away.
 *
 * `0` when nothing is reserved or the model declares no budget (BYOM/unknown).
 */
export function getImageReservedStyleSlots(model: string): number {
  const policy = IMAGE_REFERENCE_POLICY[model]
  if (!policy) return 0
  return Math.max(0, policy.maxTotal - policy.maxUser)
}

/**
 * Whether `model`'s reference cap is a quality guideline rather than a limit.
 *
 * RFC-1154: some numbers are verified provider limits, others are conservative
 * guidelines with no provider error behind them. The backend refuses to block
 * on the latter, and the UI must not either — the distinction is why this is
 * exposed rather than folded into the number.
 */
export function isImageReferenceLimitAdvisory(model: string): boolean {
  return IMAGE_REFERENCE_POLICY[model]?.advisory ?? false
}

export function getImageConstraints(model: string): ImageConstraints {
  // GPT Image 2 used to be special-cased here for its three physical output
  // shapes. It no longer needs to be: its aspect offer is declared like every
  // other model's, and its quality branch was already byte-identical to what
  // IMAGE_RESOLUTION_POLICY yields (empty `supported` -> fixed label).

  // Quality comes from the GENERATED backend capability mirror, not a
  // hand-written grid. Every native model used to be offered 512P/1K/2K/4K
  // regardless of what its provider accepts, so Nano Banana 2 Lite (1K only) failed on
  // three of its four selectable qualities with 400 INVALID_ARGUMENT
  // (T283692034). An empty `supported` means the model does not take the
  // parameter at all — show the fixed label rather than an inert picker.
  const declared = IMAGE_RESOLUTION_POLICY[model]
  if (declared) {
    return {
      qualityOptions: declared.supported.length > 0 ? [...declared.supported] : null,
      qualityDefault: declared.default ?? DEFAULT_QUALITY,
      ...(declared.supported.length === 0 ? { qualityLabel: FIXED_QUALITY_LABEL } : {}),
      aspectRatioOptions: [...offeredAspectRatios(model)],
      aspectRatioDefault: offeredAspectRatioDefault(model),
    }
  }

  // BYOM / unknown ids: fail-OPEN with the full grid. We do not know a
  // user-owned endpoint's limits and must not invent a restriction
  // (CLAUDE.md #8).
  return {
    qualityOptions: [...FULL_QUALITY_OPTIONS],
    qualityDefault: DEFAULT_QUALITY,
    aspectRatioOptions: [...offeredAspectRatios(model)],
    aspectRatioDefault: offeredAspectRatioDefault(model),
  }
}

/**
 * Snap quality + aspect ratio so the config is valid for `model`.
 *
 * - Aspect: kept if the model supports it; else mapped to the model's nearest
 *   supported ratio (orientation-preserving, numeric-nearest fallback).
 * - Quality: kept if the model offers it; else snapped to the model default
 *   (fixed-resolution models always snap to qualityDefault).
 *
 * Idempotent: passing an already-valid config returns an equal result, so it is
 * safe to call from a React effect (incl. StrictMode double-invoke).
 */
export function snapToModelConstraints(
  model: string,
  current: { quality: string; aspectRatio: AspectRatio },
): { quality: string; aspectRatio: AspectRatio } {
  const c = getImageConstraints(model)

  const aspectRatio = isAspectRatioLegalForModel(model, current.aspectRatio)
    ? current.aspectRatio
    : snapAspectRatio(current.aspectRatio, c)

  const quality =
    c.qualityOptions === null
      ? c.qualityDefault
      : c.qualityOptions.includes(current.quality)
        ? current.quality
        : c.qualityDefault

  return { quality, aspectRatio }
}

/**
 * Snap quality + aspect ratio when switching FROM one image model TO another.
 * No-op when the model is unchanged.
 *
 * Pass the user's REQUESTED values (their last explicit pick), not the values
 * a previous switch snapped them to. A value the target model accepts is kept
 * unchanged, so switching away and back returns the original choice instead of
 * a third one; only a value the target genuinely rejects moves, and the caller
 * is expected to tell the user when it does.
 */
export function snapOnModelChange(
  fromModel: string,
  toModel: string,
  requested: { quality: string; aspectRatio: AspectRatio },
): { quality: string; aspectRatio: AspectRatio } {
  if (fromModel === toModel) return requested
  return snapToModelConstraints(toModel, requested)
}

/* ── Helpers ── */

function isAspectRatioLegalForModel(model: string, aspectRatio: AspectRatio): boolean {
  const capability = MODEL_CAPABILITIES[model]
  if (!capability) return true
  // Open-ended providers take arbitrary sizes, but the backend only has an
  // exact size for the labels it offers.
  if (capability.aspectRatiosOpenEnded) return offeredAspectRatios(model).includes(aspectRatio)
  return capability.aspectRatios.includes(aspectRatio)
}

/**
 * The target's closest ratio to `current`: same orientation first, then the
 * smallest proportional (log-ratio) distance. Symmetric by construction, so a
 * portrait ratio never lands on landscape and the mapping has no hand-kept
 * table to drift out of sync with the capability record.
 */
function snapAspectRatio(current: AspectRatio, target: ImageConstraints): AspectRatio {
  const currentValue = aspectRatioValue(current)
  const fixed = target.aspectRatioOptions.filter(
    (option): option is FixedAspectRatio => option !== AUTO_ASPECT_RATIO,
  )
  if (currentValue === null || fixed.length === 0) return target.aspectRatioDefault
  const orientation = Math.sign(Math.log(currentValue))
  const sameOrientation = fixed.filter(
    (option) => Math.sign(Math.log(ASPECT_NUMERIC[option])) === orientation,
  )
  const pool = sameOrientation.length > 0 ? sameOrientation : fixed
  let best = pool[0]
  let bestDist = Math.abs(Math.log(ASPECT_NUMERIC[best] / currentValue))
  for (const option of pool.slice(1)) {
    const dist = Math.abs(Math.log(ASPECT_NUMERIC[option] / currentValue))
    if (dist < bestDist) {
      best = option
      bestDist = dist
    }
  }
  return best
}
